import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  FIELDS_UI_LABELS,
  FIELD_DATA_TYPE_CONFIG_DEFAULT,
  FIELD_THOUSANDS_GROUPING_DEFAULT,
  FormatSelectorProps,
  getCompositeFormatKey,
} from './FieldsSettingsTypes';
import { styled } from '@mui/system';
import { getDataUIlabels } from '../../../utils/getDataUIlabels';

const SelectStyled = styled(Select)({
  width: 'calc(100% - 20px)',
});

const DialogStyled = styled(Dialog)(() => ({
  '& .MuiDialog-paperWidthSm': {
    maxWidth: '500px',
  },
}));

const FormatSelector = ({
  dataTypesResponse,
  dropdownChange,
  fieldDataType,
  fieldDataTypeConfig,
  fieldLabel,
  trainingSetDataTypeConfig,
}: FormatSelectorProps): JSX.Element | null => {
  const [dateFormat, setDateFormat] = useState('');
  const [amountFormat, setAmountFormat] = useState('');
  const [numberFormat, setNumberFormat] = useState('');
  const [dialogMsgDataType, setDialogMsgDataType] = useState('');
  const [dialogTitle, setDialogTitle] = useState('');
  const [handleChangeEvent, setHandleChangeEvent] = useState<SelectChangeEvent<unknown> | null>(
    null
  );
  const [openDialog, setOpenDialog] = useState(false);
  const dataTypes = [dataTypesResponse].flat();
  const dateLabel = 'Date';
  const amountLabel = 'Amount';
  const numberLabel = 'Number';

  const [dateData] = dataTypes?.filter((data) => data?.name === dateLabel.toLowerCase());

  // Get composite options for amount and number
  const amountOptions = getDataUIlabels('amount', dataTypesResponse);
  const numberOptions = getDataUIlabels('number', dataTypesResponse);

  const handleFormatSelectorChange = (event: SelectChangeEvent<unknown>) => {
    setHandleChangeEvent(event);
    setOpenDialog(true);
  };

  const handleDialogClickOpen = (ev: React.MouseEvent<HTMLLIElement>, dialogTitle: string) => {
    setDialogMsgDataType(ev.currentTarget.textContent || '');
    setDialogTitle(dialogTitle);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDialogYes = () => {
    if (handleChangeEvent && dropdownChange) {
      dropdownChange(handleChangeEvent);
    }
    setOpenDialog(false);
  };

  useEffect(() => {
    if (fieldDataType === 'date' && fieldDataTypeConfig !== null) {
      if (fieldDataTypeConfig?.order) {
        setDateFormat(fieldDataTypeConfig.order);
      } else {
        if (trainingSetDataTypeConfig?.hasOwnProperty('date')) {
          if (
            trainingSetDataTypeConfig?.date &&
            Object.keys(trainingSetDataTypeConfig?.date).length === 0
          ) {
            setDateFormat(FIELD_DATA_TYPE_CONFIG_DEFAULT.date);
          } else {
            setDateFormat(trainingSetDataTypeConfig?.date?.order || FIELD_DATA_TYPE_CONFIG_DEFAULT.date);
          }
        } else {
          setDateFormat(FIELD_DATA_TYPE_CONFIG_DEFAULT.date);
        }
      }
    } else if (fieldDataType === 'amount' && fieldDataTypeConfig !== null) {
      // Create composite format key from decimalSymbol and thousandsGrouping
      const decimalSymbol =
        fieldDataTypeConfig?.decimalSymbol ||
        trainingSetDataTypeConfig?.amount?.decimalSymbol ||
        FIELD_DATA_TYPE_CONFIG_DEFAULT.amount;
      const thousandsGrouping =
        fieldDataTypeConfig?.thousandsGrouping ||
        trainingSetDataTypeConfig?.amount?.thousandsGrouping ||
        FIELD_THOUSANDS_GROUPING_DEFAULT;
      setAmountFormat(getCompositeFormatKey(decimalSymbol, thousandsGrouping));
    } else if (fieldDataType === 'number' && fieldDataTypeConfig !== null) {
      // Create composite format key from decimalSymbol and thousandsGrouping
      const decimalSymbol =
        fieldDataTypeConfig?.decimalSymbol ||
        trainingSetDataTypeConfig?.number?.decimalSymbol ||
        FIELD_DATA_TYPE_CONFIG_DEFAULT.number;
      const thousandsGrouping =
        fieldDataTypeConfig?.thousandsGrouping ||
        trainingSetDataTypeConfig?.number?.thousandsGrouping ||
        FIELD_THOUSANDS_GROUPING_DEFAULT;
      setNumberFormat(getCompositeFormatKey(decimalSymbol, thousandsGrouping));
    }
  }, [fieldDataType, fieldDataTypeConfig, fieldLabel, trainingSetDataTypeConfig]);

  if (dataTypesResponse === null) {
    return null;
  }

  return (
    <>
      {fieldDataType === dateLabel.toLowerCase() && (
        <SelectStyled
          data-cy="select-date"
          id="select-date"
          labelId="select-date-label"
          onChange={handleFormatSelectorChange}
          value={dateFormat}
        >
          {(dateData?.order || dateData?.options?.order) &&
            (dateData?.order || dateData?.options?.order || []).map((order: string, index: number) => {
              return (
                <MenuItem
                  data-cy="select-date-item"
                  key={index}
                  onClick={(ev) => handleDialogClickOpen(ev, dateLabel)}
                  value={order}
                >
                  {FIELDS_UI_LABELS[order]}
                </MenuItem>
              );
            })}
        </SelectStyled>
      )}
      {fieldDataType === amountLabel.toLowerCase() && (
        <SelectStyled
          data-cy="select-amount"
          id="select-amount"
          labelId="select-amount-label"
          onChange={handleFormatSelectorChange}
          value={amountFormat}
        >
          {Object.entries(amountOptions).map(([key, label], index) => (
            <MenuItem
              data-cy="select-amount-item"
              key={index}
              onClick={(ev) => handleDialogClickOpen(ev, amountLabel)}
              value={key}
            >
              {label}
            </MenuItem>
          ))}
        </SelectStyled>
      )}
      {fieldDataType === numberLabel.toLowerCase() && (
        <SelectStyled
          data-cy="select-number"
          id="select-number"
          labelId="select-number-label"
          onChange={handleFormatSelectorChange}
          value={numberFormat}
        >
          {Object.entries(numberOptions).map(([key, label], index) => (
            <MenuItem
              data-cy="select-number-item"
              key={index}
              onClick={(ev) => handleDialogClickOpen(ev, numberLabel)}
              value={key}
            >
              {label}
            </MenuItem>
          ))}
        </SelectStyled>
      )}

      <DialogStyled
        aria-describedby="custom-format-dialog-description"
        aria-labelledby="custom-format-dialog-title"
        data-cy="custom-format-dialog"
        onClose={handleDialogClose}
        open={openDialog}
      >
        <DialogTitle id="custom-format-dialog-title">{`${dialogTitle} - Confirmation`}</DialogTitle>
        <DialogContent>
          <DialogContentText id="custom-format-dialog-description">
            {`This would set the format for "${fieldLabel}" field on this
            training-set as "${dialogMsgDataType}". Do you want to proceed?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" data-cy="custom-format-dialog-no" onClick={handleDialogClose}>
            NO
          </Button>
          <Button
            autoFocus
            color="primary"
            data-cy="custom-format-dialog-yes"
            onClick={handleDialogYes}
          >
            YES
          </Button>
        </DialogActions>
      </DialogStyled>
    </>
  );
};

export default FormatSelector;
